import React from 'react';
import { debounce, get, size } from "lodash";
import classnames from 'classnames';
import Shape from "./Shape";
import Versions from "./Versions";
import { dispatch } from "@wordpress/data";
import AddToPlaylist from './AddToPlaylist';
import { Highlight } from 'react-instantsearch-dom';

class TrackRow extends React.PureComponent {
  constructor(props) {
    super(props);
    this.waveform = React.createRef();
    this.versionClickHandler = this.versionClickHandler.bind(this);
    this.getTrackKey = this.getTrackKey.bind(this);
    this.togglePlaying = this.togglePlaying.bind(this);
    this.toggleAddingTrackOn = this.toggleAddingTrackOn.bind(this);
    this.toggleAddingTrackOff = this.toggleAddingTrackOff.bind(this);
    this.setupWaveSurfer = this.setupWaveSurfer.bind(this);

    this.togglePlayingDebounced = debounce(this.togglePlaying, 500, {
      leading: true,
    });

    this.state = {
      waveSurfer: null,
      waveSurferSettingUp: false,
      isPlaying: false,
      showPlayer: false,
      isAddingTrack: false,
    };
  }

  versionClickHandler(versionNumber) {
    const { playingTrack }          = this.props;
    const { setPlayingTrack }       = dispatch('hifi');
    const { isPlaying, waveSurfer } = this.state;
    const trackKey                  = this.getTrackKey(versionNumber);
    const isMyTrackKey              = playingTrack === trackKey;

    if (isPlaying) {
      waveSurfer.pause();

      this.setState({
        isPlaying: !isPlaying,
      });
    } else {


      if (!isMyTrackKey) {
        setPlayingTrack(trackKey);
      } else {
        waveSurfer.play();

        this.setState({
          isPlaying: true,
        });
      }
    }
  }

  setupWaveSurfer() {
    const { track, version } = this.props;
    const songURL = get(version, 'hifi_song_version_url');

    this.setState({
      waveSurferSettingUp: true,
    });

    const waveSurfer = WaveSurfer.create({
      audioContext: window.HIFI_APP.audioContext, // todo, make this come from store?
      container: this.waveform.current,
      waveColor: "#420b0a",
      progressColor: "#DC2521",
      cursorColor: "transparent",
      height: 46,
    });

    waveSurfer.on("ready", () => {
      if ( this.state.isPlaying ) {
        this.state.waveSurfer.play();
      } else {
        if ( this.state.waveSurfer.isPlaying() ) {
          this.state.waveSurfer.pause();
        }

        this.state.waveSurfer.destroy();
        this.setState({
          waveSurfer: null,
        });
      }

      this.setState({
        waveSurferSettingUp: false,
      });
    });

    waveSurfer.load(songURL);

    this.setState({
      waveSurfer,
      // waveSurferSettingUp: false,
    });
  }

  getTrackKey(versionNumberOverride) {
    const { track, versionNumber } = this.props;
    const useVersionNumber = versionNumberOverride || versionNumber;
    return `${track.objectID}-${useVersionNumber}`;
  }

  toggleAddingTrackOn(versionNumberOverride) {
    const { addingTrack } = this.props;
    const { isAddingTrack } = this.state;
    const { setAddingTrack } = dispatch('hifi');

    const trackKey = this.getTrackKey(versionNumberOverride);

    if (!isAddingTrack) {
      if (addingTrack !== trackKey) {
        setAddingTrack(trackKey);
      }

      this.setState({
        isAddingTrack: !isAddingTrack,
      });
    }
  }

  toggleAddingTrackOff() {
    this.setState({
      isAddingTrack: false,
    });
  }

  togglePlaying(versionNumberOverride) {
    const { playingTrack } = this.props;
    const { isPlaying, waveSurfer, waveSurferSettingUp } = this.state;
    const { setPlayingTrack } = dispatch('hifi');

    const trackKey = this.getTrackKey(versionNumberOverride);

    if (!isPlaying) {
      const isMyTrackKey = playingTrack === trackKey;
      if (!isMyTrackKey) {
        // stop old track, before starting this one
        setPlayingTrack(trackKey);
      }

      if ( !waveSurfer ) {
        // setup then play
        this.setupWaveSurfer();
      } else {
        // play
        waveSurfer.play();
      }
    } else {
      // pause
      waveSurfer.pause();
    }

    this.setState({
      isPlaying: !isPlaying,
      showPlayer: true,
    });
  }

  componentDidUpdate(prevProps) {
    const { playingTrack, addingTrack } = this.props;
    const { isPlaying, waveSurfer } = this.state;
    const trackKey = this.getTrackKey();

    // playingTrack changed
    if (playingTrack !== prevProps.playingTrack) {
      // pause if not the playing track.

      if (playingTrack !== trackKey) {
        if (waveSurfer) {
          // if (isPlaying) {
            // this.togglePlaying();
          // }

          // pause
          if ( waveSurfer.isPlaying() ) {
            waveSurfer.pause();
          }
        }

        this.setState({
          isPlaying: false,
          showPlayer: false,
        });

      } else {
        // this is mine
        if (!isPlaying) {
          this.togglePlaying();
        }
      }
    }

    if (addingTrack !== prevProps.addingTrack && addingTrack !== trackKey) {
      this.toggleAddingTrackOff();
    }
  }

  componentWillUnmount() {
    const { isPlaying, waveSurfer } = this.state;
    if (isPlaying) {
      waveSurfer.pause();
      waveSurfer.destroy();
    }
  }

  render() {
    const { track, version, versionNumber, versionsExpanded, versionHighlightResults, toggleVersionsExpanded, energyDisplayColumn, shapeDisplayColumn } = this.props;
    const { isPlaying, showPlayer, isAddingTrack } = this.state;
    const trackKey = this.getTrackKey();

    const versionsCount = size(track.versions);
    const expanded = versionsCount > 1 && versionsExpanded;
    const isVisible = 1 === versionNumber || versionsExpanded;

    const title = 1 === versionNumber ? track.title : version.hifi_song_version_title;
    const length = 1 === versionNumber ? track.length : version.hifi_song_version_length;
    const titleVersionMatch = 1 === versionNumber ? track._highlightResult.title : versionHighlightResults;

    return (
      <div
        key={track.id}
        className={classnames({
          item: true,
          visible: isVisible,
          expanded: expanded,
          subtrack: versionNumber > 1,
        })}
      >
        <div
          className={classnames({
            "col": true,
            "first-col": true,
            "col-title": true,
            "has-versions": (versionsCount > 1),
          })}
          onClick={toggleVersionsExpanded}
          title={title}
        >
          <h2>
          <Highlight
            attribute="title"
            hit={{
              "_highlightResult": {
                "title": titleVersionMatch,
              }
            }}
          />
          </h2>
        </div>
        <div className="col col-id">{track.objectID}</div>
        <div className="col col-artist"><span>{get(track, 'artist')}</span></div>
        <div className="col col-length">{length}</div>
        {'energy' === energyDisplayColumn && <div className="col col-energy">{track.energy}</div>}
        {'bpm' === energyDisplayColumn && <div className="col col-bpm">{track.bpm}</div>}
        {'icon' === shapeDisplayColumn &&
          <div className="col col-shape">
            <Shape shapeName={track.shape_name} shapeClass={track.shape_class} />
          </div>
        }
        {'text' === shapeDisplayColumn &&
          <div className="col col-shape">{track.shape_name}</div>
        }
        <div className="col col-customizable">
          {track.customizable ? "Yes" : "No"}
        </div>
        <div className="col col-versions">
          <Versions
            versions={track.versions}
            versionNumber={versionNumber}
            versionsExpanded={versionsExpanded}
            isPlaying={isPlaying}
            clickHandler={this.versionClickHandler}
            versionsExpandedHandler={toggleVersionsExpanded}
          />
        </div>
        <div className="col col-waveform">
          <div
            className={classnames({
              "waveform": true,
              "show-player": showPlayer,
            })}
            ref={this.waveform}
          ></div>
        </div>
        <div className="col col-buttons">
          <div
            className={classnames({
              "btn": true,
              "btn-play": true,
              playing: isPlaying,
            })}
            onClick={() => {
              if ( !this.state.waveSurferSettingUp ) {
                this.togglePlayingDebounced();
              }
            }}
          >
            <span></span>
          </div>
          <a
            className={classnames({
              "btn": true,
              "btn-download": true,
            })}
            href={`/wp-content/themes/hifi-flavor/download.php?q=${version.hifi_song_version_url}`}
            download=""
          >
            <span></span>
          </a>
          <div
            className={classnames({
              "btn": true,
              "btn-add-to-playlist": true,
            })}
            onClick={() => this.toggleAddingTrackOn()}
          >
            { isAddingTrack &&
            <AddToPlaylist
              cancel={this.toggleAddingTrackOff}
              track={{
                trackId: track.objectID,
                trackKey: trackKey,
                title: track.title,
                versionId: version.hifi_song_version_id,
                versionTitle: version.hifi_song_version_title,
                versionURL: version.hifi_song_version_url,
              }}
            /> }
            <span className="icon"></span>
          </div>
        </div>
      </div>
    );
  }
}

export default TrackRow;